import { useEffect, useRef, useState } from 'react';
import { IMAGEN_MAX_BYTES, IMAGEN_TIPOS } from '@copathy/shared';
import { apiBlob } from '../lib/api';

/**
 * Las imágenes de un cuestionario: la **portada** del instrumento y la imagen que
 * acompaña al título de una **sección** (migración 031).
 *
 * ── Por qué no un `<img src="/api/…">` a secas ──
 * Es lo mismo que en `LogoCliente`: el access token vive en memoria, no en una
 * cookie, así que un `<img src>` pediría la imagen sin `Authorization` y recibiría
 * un 401. Se descarga con `fetch` y se muestra como object URL, que se libera al
 * desmontar para que recorrer el constructor no vaya dejando blobs vivos.
 *
 * ⚠️ Los `blob:` los bloquea la CSP de `helmet` cuando Express sirve la SPA —no en
 * el dev server de Vite, donde todo parece funcionar—. Está declarado en `img-src`
 * en `src/app.ts`; si esa línea se cae, esto se rompe solo en el servidor.
 */
export function ImagenCuestionario({
  ruta,
  hay,
  alt,
  className = '',
  /** Se vuelve a pedir cuando esto cambia: sirve para refrescar tras subir otra. */
  version = 0,
}: {
  ruta: string;
  hay: boolean;
  alt: string;
  className?: string;
  version?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!hay) {
      setUrl(null);
      setFallo(false);
      return;
    }
    let activo = true;
    let creada: string | null = null;

    apiBlob(ruta)
      .then((blob) => {
        if (!activo) return;
        creada = URL.createObjectURL(blob);
        setUrl(creada);
      })
      .catch(() => activo && setFallo(true));

    return () => {
      activo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [ruta, hay, version]);

  if (!hay || fallo) return null;
  if (!url) return <span className={`block animate-pulse bg-marino-100 ${className}`} />;

  /*
    `object-cover` acá, al revés que el logotipo. Un logotipo recortado deja de
    ser el logotipo; una portada, en cambio, se muestra en un marco de proporción
    fija —una banda ancha en el constructor, la cabecera de la pantalla en el
    teléfono— y meterla «entera» dejaría dos franjas vacías a los lados. El
    archivo guardado NO está recortado: el recorte es solo cómo se ve acá, y se
    puede cambiar sin volver a subir nada.
  */
  return <img src={url} alt={alt} className={`object-cover ${className}`} />;
}

/**
 * El control para poner, cambiar y quitar una de esas imágenes.
 *
 * A diferencia de `CampoLogo`, este **sube en el momento**: acá la fila ya existe
 * —el cuestionario está creado, la sección también— así que no hay razón para
 * guardar el `File` y mandarlo al confirmar un formulario. Subir enseguida
 * también evita la pregunta de qué pasa si alguien se va sin guardar.
 */
export function CampoImagen({
  titulo,
  ayuda,
  hay,
  alSubir,
  alQuitar,
  vistaPrevia,
  disposicion = 'apilada',
}: {
  titulo: string;
  ayuda: string;
  hay: boolean;
  alSubir: (f: File) => Promise<void>;
  alQuitar: () => Promise<void>;
  /** Lo que se dibuja en el marco cuando ya hay imagen. */
  vistaPrevia: React.ReactNode;
  /**
   * `apilada` —el marco arriba, los botones debajo— para la columna angosta del
   * editor. `fila` —marco a la izquierda, botones al lado— para la cabecera de
   * la pantalla, donde el alto es lo que escasea: la queja concreta de esta
   * pantalla fue que había demasiado que recorrer hacia abajo.
   */
  disposicion?: 'apilada' | 'fila';
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function elegir(f: File | null) {
    setAviso(null);
    if (!f) return;

    /*
      Se valida ANTES de subir. El servidor vuelve a validar —es el que decide—,
      pero avisar acá evita mandar 8 MB por una conexión lenta para que rebote.
    */
    if (!(IMAGEN_TIPOS as readonly string[]).includes(f.type)) {
      setAviso('Tiene que ser PNG, JPG o WebP.');
      return;
    }
    if (f.size > IMAGEN_MAX_BYTES) {
      setAviso(`No puede pesar más de ${Math.round(IMAGEN_MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }

    setOcupado(true);
    try {
      await alSubir(f);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No pudimos subir la imagen.');
    } finally {
      setOcupado(false);
      // Se limpia para que elegir DOS VECES el mismo archivo vuelva a disparar
      // el `change`. Sin esto, reintentar tras un fallo no hace nada.
      if (entrada.current) entrada.current.value = '';
    }
  }

  async function quitar() {
    setAviso(null);
    setOcupado(true);
    try {
      await alQuitar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No pudimos quitar la imagen.');
    } finally {
      setOcupado(false);
    }
  }

  const fila = disposicion === 'fila';

  /*
    ── El marco ES el botón ──
    Había un «Elegir imagen» al lado y el marco era decorativo. Sobraba: un
    rectángulo de línea discontinua con un texto adentro ya se lee como «suelta
    o elige algo acá», y en cuanto hay imagen cargada, tocarla es el gesto que
    todo el mundo prueba primero. El botón aparte era un segundo objetivo para el
    mismo acto.

    ⚠️ Sigue siendo un `<button>` de verdad y no un `div` con `onClick`: se
    alcanza con Tab, se activa con Enter y con Espacio, y anuncia qué hace. Un
    marco «clicleable» hecho con un div deja fuera a quien no usa ratón.
  */
  const marco = (
    <button
      type="button"
      disabled={ocupado}
      onClick={() => entrada.current?.click()}
      aria-label={hay ? `Cambiar la imagen: ${titulo}` : `Elegir una imagen: ${titulo}`}
      className={`group relative flex shrink-0 items-center justify-center overflow-hidden
        rounded-2xl border border-dashed border-marino-200 bg-marino-50/50 transition
        hover:border-marino-400 hover:bg-marino-50
        disabled:cursor-not-allowed
        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
        ${fila ? 'h-16 w-28' : 'h-28 w-full'} ${ocupado ? 'opacity-50' : ''}`}
    >
      {hay ? vistaPrevia : <span className="px-3 text-center text-xs text-marino-400">{ayuda}</span>}

      {/*
        Con imagen cargada hace falta decir que se puede cambiar: una foto que
        llena el marco no parece un control. Aparece al pasar por encima y, en
        teléfono —donde no hay «encima»—, el botón de «Quitar» de al lado deja
        claro que el bloque es interactivo.
      */}
      {hay && !ocupado && (
        <span
          aria-hidden="true"
          className="absolute inset-0 grid place-items-center bg-marino-900/55 text-xs
            font-semibold text-white opacity-0 transition group-hover:opacity-100"
        >
          Cambiar
        </span>
      )}

      {ocupado && (
        <span className="absolute inset-0 grid place-items-center bg-white/70 text-xs font-semibold text-marino-600">
          Subiendo…
        </span>
      )}
    </button>
  );

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-marino-400">{titulo}</p>

      <div className={fila ? 'flex items-center gap-3' : ''}>
        {marco}

        <div className={fila ? 'min-w-0 flex-1' : 'mt-2'}>
          <input
            ref={entrada}
            type="file"
            accept={IMAGEN_TIPOS.join(',')}
            className="sr-only"
            onChange={(e) => void elegir(e.target.files?.[0] ?? null)}
          />

          {hay && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={ocupado}
                onClick={() => void quitar()}
                className="rounded-2xl px-3.5 py-1.5 text-sm font-semibold text-marino-500 transition
                  hover:text-red-600 hover:underline disabled:opacity-50"
              >
                Quitar
              </button>
            </div>
          )}

          <p className={`text-xs text-marino-400 ${hay ? 'mt-1.5' : ''}`}>
            Toca el marco para {hay ? 'cambiarla' : 'elegir una'}. PNG, JPG o WebP, hasta{' '}
            {Math.round(IMAGEN_MAX_BYTES / 1024 / 1024)} MB. Se guarda completa; lo que ves
            recortado es solo el marco.
          </p>
        </div>
      </div>

      {aviso && <p className="mt-2 text-xs font-medium text-red-600">{aviso}</p>}
    </div>
  );
}
